/**
 * Field schemas for the admin CMS.
 * Each entry describes: label, type, and validation.
 * Type reference:
 *  - text: single-line string
 *  - textarea: multi-line
 *  - number, bool
 *  - select: use options list
 *  - class-select / subject-select / chapter-select: dynamic dropdown
 *  - csv: comma-separated → string[]
 *  - mcq-multiselect: pick MCQ ids
 *  - kv-list: list of {q, a} or {term, definition}
 *  - list: list of strings
 *  - youtube-url: url that resolves to video id
 *  - options-4: 4-option MCQ editor (radio for correct)
 *
 * `endpoint` is the /api/admin/{endpoint} slug.
 * `label` is the sidebar section label shown to admin.
 * `titleField` picks display column for list view.
 */
export const CMS_SECTIONS = [
  { key: "classes", label: "Classes", icon: "BookOpen", titleField: "name", fields: [
    { key: "name", label: "Class Name", type: "text", required: true },
    { key: "hindi_name", label: "Hindi Name (कक्षा)", type: "text" },
    { key: "slug", label: "URL Slug", type: "slug", from: "name" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "thumbnail", label: "Thumbnail", type: "image-upload" },
    { key: "order", label: "Display Order", type: "number", default: 0 },
    { key: "featured", label: "Featured on Homepage", type: "bool", default: false },
    { key: "active", label: "Active", type: "bool", default: true },
    { key: "meta_title", label: "SEO Title", type: "text" },
    { key: "meta_description", label: "SEO Description", type: "textarea" },
  ]},
  { key: "subjects", label: "Subjects", icon: "BookOpen", titleField: "name", fields: [
    { key: "name", label: "Subject Name", type: "text", required: true },
    { key: "hindi_name", label: "Hindi Name (विषय)", type: "text" },
    { key: "slug", label: "URL Slug", type: "slug", from: "name" },
    { key: "class_slugs", label: "Available in Classes", type: "class-multiselect", required: true },
    { key: "description", label: "Description", type: "textarea" },
    { key: "icon", label: "Lucide Icon Name", type: "text", default: "BookOpen" },
    { key: "color", label: "Accent Color", type: "select", options: ["blue","red","emerald","indigo","amber","sky","teal","green","orange","purple","cyan"], default: "blue" },
    { key: "thumbnail", label: "Thumbnail", type: "image-upload" },
    { key: "order", label: "Display Order", type: "number", default: 0 },
    { key: "featured", label: "Featured", type: "bool", default: false },
    { key: "active", label: "Active", type: "bool", default: true },
    { key: "meta_title", label: "SEO Title", type: "text" },
    { key: "meta_description", label: "SEO Description", type: "textarea" },
  ]},
  { key: "chapters", label: "Chapters", icon: "FileText", titleField: "title", fields: [
    { key: "class_slug", label: "Class", type: "class-select", required: true },
    { key: "subject_slug", label: "Subject", type: "subject-select", required: true, dependsOn: "class_slug" },
    { key: "number", label: "Chapter Number", type: "number", default: 1 },
    { key: "title", label: "Chapter Title", type: "text", required: true },
    { key: "hindi_title", label: "Hindi Title (अध्याय)", type: "text" },
    { key: "slug", label: "URL Slug", type: "slug", from: "title" },
    { key: "summary", label: "Summary", type: "textarea" },
    { key: "order", label: "Display Order", type: "number", default: 0 },
    { key: "published", label: "Published", type: "bool", default: true },
    { key: "meta_title", label: "SEO Title", type: "text" },
    { key: "meta_description", label: "SEO Description", type: "textarea" },
  ]},
  { key: "notes", label: "Notes", icon: "FileText", titleField: "title", fields: [
    { key: "class_slug", label: "Class", type: "class-select", required: true },
    { key: "subject_slug", label: "Subject", type: "subject-select", required: true, dependsOn: "class_slug" },
    { key: "chapter_slug", label: "Chapter (optional)", type: "chapter-select", dependsOn: ["class_slug","subject_slug"] },
    { key: "title", label: "Note Title", type: "text", required: true },
    { key: "hindi_title", label: "Hindi Title", type: "text" },
    { key: "slug", label: "URL Slug", type: "slug", from: "title" },
    { key: "intro", label: "Short Introduction", type: "textarea" },
    { key: "content", label: "Full Content", type: "textarea", rows: 10, required: true },
    { key: "key_points", label: "Key Points (one per line)", type: "list" },
    { key: "exam_tips", label: "Exam Tips (one per line)", type: "list" },
    { key: "definitions", label: "Definitions", type: "kv-list", keys: ["term","definition"] },
    { key: "youtube_id", label: "Related YouTube Video", type: "youtube-url" },
    { key: "featured_image", label: "Featured Image", type: "image-upload" },
    { key: "pdf_url", label: "PDF Attachment", type: "pdf-upload" },
    { key: "published", label: "Published", type: "bool", default: true },
    { key: "meta_title", label: "SEO Title", type: "text" },
    { key: "meta_description", label: "SEO Description", type: "textarea" },
  ]},
  { key: "mcqs", label: "MCQ Questions", icon: "HelpCircle", titleField: "question", fields: [
    { key: "class_slug", label: "Class", type: "class-select", required: true },
    { key: "subject_slug", label: "Subject", type: "subject-select", required: true, dependsOn: "class_slug" },
    { key: "chapter_slug", label: "Chapter (optional)", type: "chapter-select", dependsOn: ["class_slug","subject_slug"] },
    { key: "question", label: "Question", type: "textarea", required: true },
    { key: "options", label: "4 Options (select the correct one)", type: "options-4", required: true },
    { key: "explanation", label: "Explanation", type: "textarea" },
    { key: "difficulty", label: "Difficulty", type: "select", options: ["easy","medium","hard"], default: "medium" },
    { key: "published", label: "Published", type: "bool", default: true },
  ]},
  { key: "important-questions", label: "Important Questions", icon: "ClipboardList", titleField: "question", fields: [
    { key: "class_slug", label: "Class", type: "class-select", required: true },
    { key: "subject_slug", label: "Subject", type: "subject-select", required: true, dependsOn: "class_slug" },
    { key: "chapter_slug", label: "Chapter (optional)", type: "chapter-select", dependsOn: ["class_slug","subject_slug"] },
    { key: "question", label: "Question", type: "textarea", required: true },
    { key: "answer", label: "Answer / Solution", type: "textarea" },
    { key: "question_type", label: "Type", type: "select", options: ["very_short","short","long"], default: "short" },
    { key: "marks", label: "Marks", type: "number", default: 2 },
    { key: "year", label: "Year (optional)", type: "number" },
    { key: "published", label: "Published", type: "bool", default: true },
  ]},
  { key: "pyq", label: "Previous Year Qs", icon: "FileText", titleField: "question", fields: [
    { key: "class_slug", label: "Class", type: "class-select", required: true },
    { key: "subject_slug", label: "Subject", type: "subject-select", required: true, dependsOn: "class_slug" },
    { key: "chapter_slug", label: "Chapter (optional)", type: "chapter-select", dependsOn: ["class_slug","subject_slug"] },
    { key: "year", label: "Year", type: "number", required: true },
    { key: "question", label: "Question", type: "textarea", required: true },
    { key: "answer", label: "Answer / Solution", type: "textarea" },
    { key: "marks", label: "Marks", type: "number" },
    { key: "pdf_url", label: "PDF (optional)", type: "pdf-upload" },
    { key: "published", label: "Published", type: "bool", default: true },
  ]},
  { key: "quizzes", label: "Quizzes", icon: "HelpCircle", titleField: "title", fields: [
    { key: "title", label: "Quiz Title", type: "text", required: true },
    { key: "hindi_title", label: "Hindi Title", type: "text" },
    { key: "slug", label: "URL Slug", type: "slug", from: "title" },
    { key: "class_slug", label: "Class", type: "class-select", required: true },
    { key: "subject_slug", label: "Subject", type: "subject-select", required: true, dependsOn: "class_slug" },
    { key: "chapter_slug", label: "Chapter (optional)", type: "chapter-select", dependsOn: ["class_slug","subject_slug"] },
    { key: "description", label: "Description", type: "textarea" },
    { key: "duration_minutes", label: "Duration (minutes)", type: "number", default: 10 },
    { key: "mcq_ids", label: "Select MCQ Questions", type: "mcq-multiselect" },
    { key: "published", label: "Published", type: "bool", default: true },
  ]},
  { key: "tests", label: "Online Tests", icon: "ClipboardList", titleField: "title", fields: [
    { key: "title", label: "Test Name", type: "text", required: true },
    { key: "slug", label: "URL Slug", type: "slug", from: "title" },
    { key: "class_slug", label: "Class", type: "class-select", required: true },
    { key: "subject_slug", label: "Subject (optional)", type: "subject-select", dependsOn: "class_slug" },
    { key: "chapter_slug", label: "Chapter (optional)", type: "chapter-select", dependsOn: ["class_slug","subject_slug"] },
    { key: "description", label: "Description", type: "textarea" },
    { key: "duration_minutes", label: "Duration (minutes)", type: "number", default: 60 },
    { key: "total_marks", label: "Total Marks", type: "number", default: 100 },
    { key: "passing_marks", label: "Passing Marks", type: "number", default: 33 },
    { key: "mcq_ids", label: "Select MCQ Questions", type: "mcq-multiselect" },
    { key: "published", label: "Published", type: "bool", default: true },
  ]},
  { key: "videos", label: "YouTube Videos", icon: "Video", titleField: "title", fields: [
    { key: "youtube_url", label: "YouTube URL", type: "youtube-url-full", required: true, autoSetField: "youtube_id" },
    { key: "youtube_id", label: "Video ID (auto)", type: "text", readOnly: true },
    { key: "title", label: "Video Title", type: "text", required: true },
    { key: "hindi_title", label: "Hindi Title", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "class_slug", label: "Class", type: "class-select" },
    { key: "subject_slug", label: "Subject", type: "subject-select", dependsOn: "class_slug" },
    { key: "category", label: "Category", type: "text" },
    { key: "teacher", label: "Teacher", type: "text", default: "Faiz Sir" },
    { key: "thumbnail", label: "Custom Thumbnail (blank = use YouTube thumbnail)", type: "image-upload" },
    { key: "published_at", label: "Published Date", type: "text" },
    { key: "order", label: "Display Order", type: "number", default: 0 },
    { key: "featured", label: "Featured on Homepage", type: "bool", default: false },
    { key: "active", label: "Active", type: "bool", default: true },
  ]},
  { key: "blog", label: "Blog Posts", icon: "FileText", titleField: "title", fields: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "hindi_title", label: "Hindi Title", type: "text" },
    { key: "slug", label: "URL Slug", type: "slug", from: "title" },
    { key: "excerpt", label: "Excerpt", type: "textarea" },
    { key: "content", label: "Full Content", type: "textarea", rows: 10, required: true },
    { key: "category", label: "Category", type: "text", default: "General" },
    { key: "author", label: "Author", type: "text", default: "VR Classes Team" },
    { key: "featured_image", label: "Featured Image", type: "image-upload" },
    { key: "faqs", label: "FAQs", type: "kv-list", keys: ["q","a"] },
    { key: "published", label: "Published", type: "bool", default: true },
    { key: "published_at", label: "Publish Date", type: "text" },
    { key: "meta_title", label: "SEO Title", type: "text" },
    { key: "meta_description", label: "SEO Description", type: "textarea" },
  ]},
  { key: "courses", label: "Courses", icon: "GraduationCap", titleField: "name", fields: [
    { key: "name", label: "Course Name", type: "text", required: true },
    { key: "hindi_name", label: "Hindi Name", type: "text" },
    { key: "slug", label: "URL Slug", type: "slug", from: "name" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "price", label: "Price (INR)", type: "number", required: true, default: 199 },
    { key: "original_price", label: "Original Price (INR)", type: "number", default: 999 },
    { key: "thumbnail", label: "Course Thumbnail", type: "image-upload" },
    { key: "course_url", label: "Course URL/Page", type: "text", default: "/english-course" },
    { key: "cta_text", label: "CTA Button Text", type: "text", default: "BUY COURSE NOW" },
    { key: "benefits", label: "Benefits (one per line)", type: "list" },
    { key: "featured", label: "Featured", type: "bool", default: true },
    { key: "published", label: "Published", type: "bool", default: true },
  ]},
  { key: "faqs", label: "FAQs", icon: "HelpCircle", titleField: "question", fields: [
    { key: "question", label: "Question", type: "textarea", required: true },
    { key: "answer", label: "Answer", type: "textarea", required: true },
    { key: "category", label: "Category", type: "text", default: "General" },
    { key: "order", label: "Display Order", type: "number", default: 0 },
    { key: "published", label: "Published", type: "bool", default: true },
  ]},
];

export const SECTION_MAP = Object.fromEntries(CMS_SECTIONS.map((s) => [s.key, s]));
